import { useMemo } from "react";
import { Checkbox as ZhCheckbox } from "zhihao-ui-react";

import {
    authList,
    type AUTH,
    type AUTH_OPTION
} from "@/views/pages/manageCenter/systemManage/role/components/authList";

export interface RolePermissionSelectorProps {
    value?: string[];
    onChange?: (value: string[]) => void;
    disabled?: boolean;
}

interface PermissionRow {
    middle: AUTH;
    leaf: AUTH;
}

/** 把旧 Vue 权限树转换为“一级模块/二级模块/操作项”矩阵行。 */
const collectRows = (group: AUTH): PermissionRow[] =>
    (group.children || []).flatMap(middle =>
        middle.children?.length ? middle.children.map(leaf => ({ middle, leaf })) : [{ middle, leaf: middle }]
    );

/** 收集节点自身及所有后代操作项，用于计算全选和半选状态。 */
const collectOptions = (node: AUTH): AUTH_OPTION[] => [
    ...(node.authOptions || []),
    ...(node.children || []).flatMap(collectOptions)
];

/** 角色权限矩阵，保持 Vue 旧版表格的行合并和操作项顺序。 */
export function RolePermissionSelector({ value = [], onChange, disabled = false }: RolePermissionSelectorProps) {
    const selected = useMemo(() => new Set(value), [value]);
    const options = useMemo(() => authList.flatMap(collectOptions), []);
    const groups = useMemo(
        () => authList.map(group => ({ group, rows: collectRows(group), options: collectOptions(group) })),
        []
    );

    // 统一按旧 Vue 的 authList 顺序提交，避免权限编码顺序变化造成无意义 diff。
    const updateSelection = (next: Set<string>) => {
        onChange?.(options.filter(option => next.has(option.auth)).map(option => option.auth));
    };

    const getState = (items: AUTH_OPTION[]) => {
        const checkedCount = items.filter(option => selected.has(option.auth)).length;
        return {
            checked: items.length > 0 && checkedCount === items.length,
            indeterminate: checkedCount > 0 && checkedCount < items.length
        };
    };

    const toggleOptions = (items: AUTH_OPTION[], checked: boolean) => {
        const next = new Set(selected);
        items.forEach(option => (checked ? next.add(option.auth) : next.delete(option.auth)));
        updateSelection(next);
    };

    const toggleOption = (items: AUTH_OPTION[], option: AUTH_OPTION, checked: boolean) => {
        const next = new Set(selected);
        if (checked) {
            next.add(option.auth);
            // 与 Vue 旧版一致：勾选管理动作时自动勾选同一行的查看动作。
            const readOption = items[0];
            if (readOption && option !== readOption) next.add(readOption.auth);
        } else {
            next.delete(option.auth);
            // 取消查看时清除同一行的全部操作权限。
            if (option === items[0]) items.forEach(item => next.delete(item.auth));
        }
        updateSelection(next);
    };

    return (
        <div aria-label="role-permission-selector" className="role-permission-selector">
            {/* 保留旧 Vue 权限矩阵的全局选择入口，便于一次性授予或清空全部权限。 */}
            <ZhCheckbox
                aria-label="全选"
                checked={getState(options).checked}
                disabled={disabled}
                indeterminate={getState(options).indeterminate}
                onChange={event => toggleOptions(options, event.target.checked)}
            >
                全选
            </ZhCheckbox>
            {groups.map(({ group, rows, options: groupOptions }) => {
                const groupState = getState(groupOptions);
                return (
                    <div key={group.label} className="role-permission-group">
                        <ZhCheckbox
                            aria-label={group.label}
                            checked={groupState.checked}
                            disabled={disabled}
                            indeterminate={groupState.indeterminate}
                            onChange={event => toggleOptions(groupOptions, event.target.checked)}
                        >
                            {group.label}
                        </ZhCheckbox>
                        <table className="role-permission-table">
                            <tbody>
                                {rows.map(({ middle, leaf }, rowIndex) => {
                                    const leafOptions = leaf.authOptions || [];
                                    const middleOptions = collectOptions(middle);
                                    const middleState = getState(middleOptions);
                                    const startsMiddle = rowIndex === 0 || rows[rowIndex - 1]?.middle !== middle;
                                    const middleRowSpan = rows.filter(row => row.middle === middle).length;
                                    return (
                                        <tr key={`${group.label}-${middle.label}-${leaf.label}-${rowIndex}`}>
                                            {startsMiddle ? (
                                                <th rowSpan={middleRowSpan}>
                                                    <ZhCheckbox
                                                        aria-label={`${group.label} / ${middle.label}`}
                                                        checked={middleState.checked}
                                                        disabled={disabled}
                                                        indeterminate={middleState.indeterminate}
                                                        onChange={event =>
                                                            toggleOptions(middleOptions, event.target.checked)
                                                        }
                                                    >
                                                        {middle.label}
                                                    </ZhCheckbox>
                                                </th>
                                            ) : null}
                                            {leaf !== middle ? (
                                                <th>
                                                    <ZhCheckbox
                                                        aria-label={`${group.label} / ${middle.label} / ${leaf.label}`}
                                                        checked={getState(leafOptions).checked}
                                                        disabled={disabled}
                                                        indeterminate={getState(leafOptions).indeterminate}
                                                        onChange={event =>
                                                            toggleOptions(leafOptions, event.target.checked)
                                                        }
                                                    >
                                                        {leaf.label}
                                                    </ZhCheckbox>
                                                </th>
                                            ) : null}
                                            <td colSpan={leaf === middle ? 2 : 1}>
                                                {leafOptions.map(option => (
                                                    <ZhCheckbox
                                                        aria-label={`${group.label} / ${middle.label} / ${leaf.label} - ${option.label}`}
                                                        key={option.auth}
                                                        checked={selected.has(option.auth)}
                                                        className="role-permission-option"
                                                        disabled={disabled}
                                                        onChange={event =>
                                                            toggleOption(leafOptions, option, event.target.checked)
                                                        }
                                                    >
                                                        {option.label}
                                                    </ZhCheckbox>
                                                ))}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
}

export default RolePermissionSelector;
